/* eslint-disable no-console */
/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable class-methods-use-this */
/* eslint-disable no-unused-vars */
import { StyleType } from "@iventis/domain-model/model/styleType";
import { Config, Flags, Logger, LogLevel, PixelStreaming } from "@epicgames-ps/lib-pixelstreamingfrontend-ue5.7";
import { Feature, FeatureCollection, GeoJsonProperties, Geometry, Position } from "geojson";
import { Subject, Subscription } from "rxjs";
import { filter, skipWhile, timeout } from "rxjs/operators";
import { BBox } from "@turf/helpers";
import { createLog } from "@iventis/observability-and-monitoring";
import { AnySupportedGeometry, MapObjectProperties } from "@iventis/map-types";
import { DigitalTwinCameraMode } from "@iventis/domain-model/model/digitalTwinCameraMode";
import { DigitalTwinLicence } from "@iventis/domain-model/model/digitalTwinLicence";
import { DIGITAL_TWIN_TIMEOUT, getDigitalTwinSettings } from "@iventis/digital-twin";
import { ClickEvent, CompositionMapObject, Listener, MapCursor, MoveEvent, ReflectLocalObjectsOptions, StylePropertyToValueMap, TypedFeature } from "../../types/internal";
import { MapModuleLayer, MappingEngine, MapState, Source, StoredBounds, StoredPosition, UnionOfStyles } from "../../types/store-schema";
import { EngineInterpreter } from "../engine-generic";
import { UnrealEngineErrors, UnrealEngineOptions } from "./engine-unreal.types";
import { EngineUnrealEventName, EngineUnrealWebSocketEvents, ExtractPayloadFromUnrealEvent } from "./engine-unreal-websockets-message";
import { MapStoreEventType } from "../../types/events-store";
import { AnyLayerStyle } from "../../types/style-types";
import { Layer } from "../generic-layers/layer";

// Set log verbosity of pixel streaming library
Logger.InitLogging(LogLevel.Error, true);

/**
 * Here lies the lower-level abstractions
 * Specific implementation of the engine interpreter in the unreal flavour
 */
export class UnrealEngine extends EngineInterpreter {
    private pixelStreaming: PixelStreaming;

    private pixelStreamingEvents: Subject<EngineUnrealWebSocketEvents> = new Subject<EngineUnrealWebSocketEvents>();

    private containerResizeObserver: ResizeObserver;

    /** Used to dismiss the AFK warning produced from pixel streaming.
     * We call this OnMove which is why it needs to be defaulted to an empty function.
     * For when we are not afk.
     * */
    private dismissAfkFunc: () => void = () => {};

    private projectId: string;

    private initialPosition: StoredPosition;

    private apiURL: string;

    private initialisingAbortController: AbortController;

    public getMappingEngine(): MappingEngine {
        return MappingEngine.Unreal;
    }

    private getApiKey: () => Promise<string>;

    private showReconnecting: (isConnecting: boolean) => void;

    private exitMappingEngine: (error: UnrealEngineErrors) => void;

    // Store all subscriptions used, so they can be cleaned up on destroy
    private subscriptions: Subscription[] = [];

    private removeSubscription(subscription: Subscription) {
        if (!subscription.closed) {
            subscription.unsubscribe();
        }
        if (this.subscriptions.indexOf(subscription) > -1) {
            this.subscriptions.splice(this.subscriptions.indexOf(subscription), 1);
        }
    }

    public async getPosition(): Promise<StoredPosition> {
        // Request venue id for bookmarks
        this.sendMessageToUnrealEngine({
            eventName: "REQUEST_VENUE_ID",
            payload: null,
        });

        // Get location
        const location = await this.askUnrealEngine({ eventName: "REQUEST_LOCATION", payload: null }, "LOCATION");
        // Check if either lng, lat or zoom is null. If they are it is an invalid location
        const isInvalidLocation = location.lng == null || location.lat == null;
        if (isInvalidLocation) {
            // If invalid location just return the previous location, otherwise when transitioning from Unreal to Mapbox, Mapbox will crash
            return this.getCurrentState().position.value;
        }

        return {
            lat: location.lat,
            lng: location.lng,
            zoom: location.zoom,
            bearing: location.bearing,
            pitch: location.pitch,
            altitude: location.altitude,
            source: Source.MAP,
        };
    }

    protected async getBounds(): Promise<StoredBounds> {
        const bounds = await this.askUnrealEngine({ eventName: "REQUEST_BOUNDS", payload: null }, "BOUNDS");
        return { bounds: [bounds.coordinates.map((bound) => [bound.lng, bound.lat])], source: Source.MAP };
    }

    public getRotation(): number {
        throw new Error("Implementation not methodical.");
    }

    protected onImageRequired(): Listener {
        // Never required here
        return this.emptyListener();
    }

    protected onMoveEnd(callback: () => void): Listener {
        return this.listenerFromUnrealEngine("MOVE_END", () => {
            // When movement ends, we can dismiss the AFK warning if it is active
            this.dismissAfkFunc();
            callback();
        });
    }

    protected onMouseMove(
        callback: (event: MoveEvent) => void,
        options?: { queryRadius?: number | undefined; layerId?: string | undefined; disableQuerying?: boolean | undefined } | undefined
    ): Listener {
        // ToDo: Listen to mouse move events from engine, if needed
        return this.emptyListener();
    }

    protected onMouseUp(callback: (event: MoveEvent) => void): Listener {
        // ToDo: Listen to mouse move events from engine, if needed
        return this.emptyListener();
    }

    protected onMouseUpOutsideMap(callback: (event: MouseEvent) => void): Listener {
        // ToDo: Listen to mouse move events from engine, if needed
        return this.emptyListener();
    }

    protected onClick(callback: (event: ClickEvent) => void, layerId?: string | undefined): Listener {
        // ToDo: Listen to click events from engine, if needed
        return this.emptyListener();
    }

    protected onMouseDown(callback: (event: ClickEvent) => void, layerIds?: string[]): Listener {
        // ToDo: Listen to mouse down events from engine, if needed
        return this.emptyListener();
    }

    public onMapIdle(callback: (event: MoveEvent) => void, options: { disableMapQuerying: boolean; listenOnce: boolean }): Listener {
        // ToDo: Listen to map idle events from engine, if needed
        return this.emptyListener();
    }

    protected onDoubleClick(callback: (event: ClickEvent) => void): Listener {
        // ToDo: Listen to double click events from engine, if needed
        return this.emptyListener();
    }

    protected onRightClick(callback: (event: ClickEvent) => void): Listener {
        // ToDo: Listen to right click events from engine, if needed
        return this.emptyListener();
    }

    protected onMouseOut(callback: (event: MoveEvent) => void): Listener {
        // ToDo: Listen to mouse out events from engine, if needed
        return this.emptyListener();
    }

    protected onMouseOver(callback: (event: MoveEvent) => void): Listener {
        return this.emptyListener();
    }

    protected OnMapMoveStart(callback: () => void): Listener {
        // ToDo: Listen to map move start events from engine, if needed
        return this.emptyListener();
    }

    protected OnMapMoveEnd(callback: () => void): Listener {
        // ToDo: Listen to map move start events from engine, if needed
        return this.emptyListener();
    }

    protected onPanStart(callback: () => void): Listener {
        // ToDo: Listen to pan start events from engine
        return this.emptyListener();
    }

    protected onPanEnd(callback: () => void): Listener {
        // ToDo: Listen to pan events from engine
        return this.emptyListener();
    }

    protected onZoomStart(callback: () => void): Listener {
        // ToDo: Listen to Zoom start events from engine
        return this.emptyListener();
    }

    protected onZoomEnd(callback: () => void): Listener {
        // ToDo: Listen to pan events from engine
        return this.emptyListener();
    }

    protected onMapRotateStart(callback: () => void): Listener {
        return this.emptyListener();
    }

    protected onMapRotateEnd(callback: (bearing: number) => void): Listener {
        return this.emptyListener();
    }

    protected onFirstRender(callback: () => void): void {
        // ToDo: Could enhance this one so the engine lets us know when its ready
        // We do not want the map loaded to execute until every has finished initialising, so we use setTimeout
        // with zero to ensure it goes to the back of the call stack
        setTimeout(() => callback(), 0);
    }

    protected removeOnMapLoadedListener(): void {
        // ToDo: Send send event to engine
    }

    protected reflectLocalObjects(options: ReflectLocalObjectsOptions): void {
        // ToDo: Send send event to engine
    }

    protected reflectSpecificLocalLayers(layersIds: string[], options: ReflectLocalObjectsOptions) {
        // ToDo: Send send event to engine
    }

    protected hideRemoteObjects(): void {
        // ToDo: Send send event to engine
    }

    protected ensureLayerInMap(layer: MapModuleLayer): void {
        // ToDo: Send send event to engine
    }

    protected queryMapObjects(x: number, y: number, radius?: number | undefined): Feature<Geometry, GeoJsonProperties>[] {
        throw new Error("queryMapObjects is not implemented in Engine Unreal");
    }

    protected reflectSource(sourceId: string, value: FeatureCollection<Geometry, GeoJsonProperties>): void {
        // ToDo: Send send event to engine
    }

    protected reflectAdditionalSources(layer: MapModuleLayer, values: Feature<AnySupportedGeometry, MapObjectProperties>[], allowTextOnSelectedObjects: boolean): void {
        // ToDo: Send send event to engine
    }

    protected async addImage(url: string | HTMLImageElement, id: string): Promise<void> {
        // ToDo: Send send event to engine
    }

    protected isImageAdded(id: string): boolean {
        return true;
    }

    protected addCoordinateHandleLayer(): void {
        // ToDo: Send send event to engine
    }

    protected removeCoordinateHandleLayer(): void {
        // ToDo: Send send event to engine
    }

    protected addWaypointsLayer(): void {
        // ToDo: Send send event to engine
    }

    protected removeWaypointLayer() {
        // ToDo: Send send event to engine
    }

    protected addMidpointHandleLayer(): void {
        // ToDo: Send send event to engine
    }

    protected removeMidpointHandleLayer(): void {
        // ToDo: Send send event to engine
    }

    protected addContinueDrawingLayer(): void {
        throw new Error("Method not implemented.");
    }

    protected setContinueDrawingButtonRotation(): void {
        throw new Error("Method not implemented.");
    }

    protected addCoordinateDeleteLayer(): void {
        // ToDo: Send send event to engine
    }

    protected addRotateHandleLayer() {
        // ToDo: Send send event to engine
    }

    protected removeCoordinateDeleteLayer(): void {
        // ToDo: Send send event to engine
    }

    protected addTraceLineLayer(): void {
        // ToDo: Send send event to engine
    }

    protected disableDragPan() {
        // ToDo: Send send event to engine
    }

    public enableDragPan() {
        // ToDo: Send send event to engine
    }

    protected disableMapRotation() {
        // ToDo: Send send event to engine
    }

    protected enableMapRotation() {
        // ToDo: Send send event to engine
    }

    public setCursor(cursorState: MapCursor): void {
        // ToDo: Send send event to engine
    }

    public project(coordinate: Position): [number, number] {
        throw new Error("project is not implemented in Engine Unreal");
    }

    protected unproject(coordinate: [number, number]): Position {
        throw new Error("unproject is not implemented in Engine Unreal");
    }

    protected addLayer(layer: MapModuleLayer, refreshMap?: boolean | undefined): void {
        // ToDo: Send send event to engine
    }

    protected hideLayer(id: string): void {
        this.sendMessageToUnrealEngine({
            eventName: "SET_NODE_VISIBLE",
            payload: {
                nodeId: id,
                visible: false,
            },
        });
    }

    protected showLayer(id: string): void {
        this.sendMessageToUnrealEngine({
            eventName: "SET_NODE_VISIBLE",
            payload: {
                nodeId: id,
                visible: true,
            },
        });
    }

    protected removeLayer(id: string): void {
        // ToDo: Send send event to engine
    }

    protected hideLocalObjects(layerId: string, objectIds: string[]): void {
        // ToDo: Send event to engine
    }

    protected unhideLocalObjects(layerId: string, objectIds: string[]): void {
        // ToDo: Send event to engine
    }

    protected removeTextLabels(removals: { objectId: string; layerId: string }[]): void {
        // ToDo: Send send event to engine
    }

    protected updateStyle<TStyle extends UnionOfStyles = UnionOfStyles>(layerId: string, styleType: StyleType, styleChanges: StylePropertyToValueMap<TStyle>[]): void {
        // ToDo: Send send event to engine
    }

    protected checkIfLayerExists(layerId: string): boolean {
        return true;
    }

    protected localSourceIsAdded(sourceId: string, localOnlyLayer: boolean): boolean {
        return false;
    }

    protected addHighlightLayer(layer: MapModuleLayer): void {
        // ToDo: Send send event to engine
    }

    protected removeHighlightLayer(layerId: string[]): void {
        // ToDo: Send send event to engine
    }

    protected highlightMapObjects(remoteMapObjectIds: string[], localMapObjectIds: string[], selectedLayerIds: string[]): void {
        // ToDo: Send send event to engine
    }

    protected async refreshTileSources(sourceNames: string[]): Promise<void> {
        // ToDo: Send send event to engine
    }

    protected configureTerrain3D(enabled: boolean, exaggeration: number): void {
        // ToDo: Send send event to engine
    }

    protected configureGlobeProjection(enabled: boolean): void {
        // ToDo: Send send event to engine
    }

    public setPosition(lat: number, lng: number, bearing: number, pitch: number, zoom: number, smooth = false, cameraMode = null, altitude = 0): void {
        this.sendEventToStore({
            eventType: MapStoreEventType.SET_CAMERA_MODE,
            payload: cameraMode || DigitalTwinCameraMode.Orbit,
        });

        this.sendMessageToUnrealEngine({
            eventName: "SET_LOCATION",
            payload: {
                lat,
                lng,
                bearing,
                pitch,
                zoom,
                smooth,
                altitude,
                cameraMode,
            },
        });
    }

    public setBearing(bearing: number): void {}

    public onResize(): void {
        // ToDo: Send send event to engine
    }

    protected getMapCentre(cb: (pos: Position) => void, isMoving?: boolean): void {
        // ToDo: Send send event to engine
    }

    public async getSnapshotDataUrl(): Promise<string> {
        let screenshotDataUrl = "data:image/png;base64,";

        // Screenshots from Unreal Engine are sent in chunks. This is because webrtc has a message size limit that our images exceed.
        // This function will request a screenshot and listen to all screenshot events from the unreal engine, combining the chunks until the full image is received
        // and resolving the promise with the completed image data url.
        const resultantUrl = await this.askUnrealEngine({ eventName: "REQUEST_SCREENSHOT", payload: null }, "SCREENSHOT", 15000, (resolve: (data: string) => void, response) => {
            // Combine chunks
            screenshotDataUrl += response.chunkData;

            if (response.chunkIndex === response.totalChunks) {
                resolve(screenshotDataUrl);
            }
        });

        return resultantUrl;
    }

    protected addTextboxLayer(layerId: string, sourceId = layerId): void {
        // ToDo: Send send event to engine
    }

    public setBounds(bounds: number[][][]): void {
        throw new Error("Method not implemented.");
    }

    public setBoundsWithBounds(bounds: [number, number, number, number], duration?: number, maxZoom?: number, padding?: number): void {
        throw new Error("Method not implemented.");
    }

    public updateBoundsForSource(sourceId: string, bounds?: BBox): void {
        throw new Error("Method not implemented.");
    }

    constructor({
        store,
        container,
        eventStream,
        preview,
        assetOptions,
        modifierKeys,
        getIpAddress,
        getApiKey,
        projectId,
        apiURL,
        devTools,
        getAttributeListItems,
        routeApiFunctions,
        user,
        isExternalUser,
        showReconnecting,
        exitMappingEngine,
    }: UnrealEngineOptions) {
        super({ store, container, eventStream, preview, modifierKeys, assetOptions, devTools, getAttributeListItems, routeApiFunctions, user, isExternalUser });
        this.initialisingAbortController = new AbortController();
        this.getIpAddress = getIpAddress;
        this.getApiKey = getApiKey;
        this.projectId = projectId;
        this.apiURL = apiURL;
        this.showReconnecting = showReconnecting;
        this.exitMappingEngine = exitMappingEngine;
    }

    private pixelStreamingTimeout?: NodeJS.Timeout;

    public async initialise(digitalTwinLicence: DigitalTwinLicence): Promise<void> {
        const ipAddress = await this.getIpAddress(digitalTwinLicence);
        const state = this.getCurrentState();

        // Pixel streaming connects via webcockets, convert http protocol to ws
        const urlObject = new URL(ipAddress);

        if (this.initialisingAbortController.signal.aborted) return;

        // When we are using a local digital twin, we connect via http and ws, but when we are using a deployed digital twin, we connect via https and wss
        urlObject.protocol = urlObject.protocol === "https:" ? "wss:" : "ws:";
        try {
            this.pixelStreaming = new PixelStreaming(
                new Config({
                    initialSettings: getDigitalTwinSettings(urlObject.toString()),
                }),
                {
                    videoElementParent: this.container,
                }
            );
        } catch (e) {
            // failed to create pixel streaming instance, this can happen if the url is invalid
            this.exitMappingEngine(UnrealEngineErrors.CONNECTION_FAILED);
            return;
        }

        if (this.initialisingAbortController.signal.aborted) return;

        const listener = this.onPixelStreamingConnectionSuccess.bind(this);
        this.pixelStreaming.addEventListener("playStream", listener);

        // Observe whenever the container changes size
        this.containerResizeObserver = new ResizeObserver(this.setStreamResolution.bind(this));
        this.containerResizeObserver.observe(this.container);

        this.pixelStreaming.addEventListener("playStreamRejected", () => {
            // This is where playing the video stream has failed, this can occur due to user interaction requirements in browsers
            this.exitMappingEngine(UnrealEngineErrors.CONNECTION_FAILED);
        });

        this.pixelStreaming.addEventListener("subscribeFailed", ({ data: { message } }) => {
            // This is where we can catch subscription failures, this can occur because the signalling server has rejected us
            this.onPixelStreamingConnectionFailure(message);
        });

        this.pixelStreaming.addEventListener("afkTimedOut", () => {
            // Called once AFK Countdown has completed, we should disconnect the user here
            this.exitMappingEngine(UnrealEngineErrors.AFK_TIMEOUT);
        });

        this.pixelStreaming.addEventListener("afkWarningActivate", ({ data: { dismissAfk } }) => {
            // Called once the user is now considered AFK, we can use this to show a warning message
            // dismissAfk must be called to stop the countdown and reset the AFK timer
            this.dismissAfkFunc = dismissAfk;
        });

        this.pixelStreaming.addEventListener("afkWarningDeactivate", () => {
            // Called once the user is no longer considered AFK, we can use this to hide a warning message
            this.dismissAfkFunc = () => {};
        });

        this.pixelStreaming.addEventListener("streamerListMessage", ({ data: { autoSelectedStreamerId } }) => {
            // Fired on connecting to the signalling server, the pixel streamer will automatically consume this to connect to the streamer
            const state = this.getCurrentState();

            // If there is no selected streamer ID, the pixel streaming player will automatically retry for us until a streamer is selected
            if (autoSelectedStreamerId.length === 0) {
                if (this.pixelStreamingTimeout) {
                    clearTimeout(this.pixelStreamingTimeout);
                }
                // Once we have reached our retry attempts, this event will be called and this will return false
                if (this.pixelStreaming.isReconnecting()) {
                    // We only want to show the oops screen if the user was already connected
                    if (state.digitalTwinEngineData.userConnectedToDigitalTwin) {
                        this.showReconnecting(true);
                    }
                } else {
                    // We want to hide the "oops" screen and throw an error toast and return to mapbox
                    this.showReconnecting(false);
                    this.exitMappingEngine(UnrealEngineErrors.ENGINE_ERROR);
                }
            } else {
                // If we have a selected streamer, we want to start playing the stream right away
                this.showReconnecting(false);
                // Start timeout. If we do not connect within 15 seconds of the streamer selected, we want to show the connection test screen
                this.pixelStreamingTimeout = setTimeout(() => {
                    this.pixelStreaming.removeEventListener("playStream", listener);
                    this.pixelStreaming.signallingProtocol.sendMessage({ type: "unsubscribe" });
                    this.pixelStreaming.disconnect();
                    this.exitMappingEngine(UnrealEngineErrors.CONNECTION_FAILED);
                }, DIGITAL_TWIN_TIMEOUT);
                this.pixelStreaming.play();
            }
        });

        this.pixelStreaming.signallingProtocol.transport.addListener("error", () => {
            this.onPixelStreamingConnectionFailure("Failed to connect websocket to Unreal Engine.");
        });

        this.pixelStreaming.addResponseEventListener("handleResponses", this.onMessageReceivedFromUnrealEngine.bind(this));

        document.addEventListener("pointerlockchange", this.onPointerLockChange);

        this.initialPosition = state.position.value;

        this.initialiseUnrealListeners();
        this.initialiseListeners();
        this.saveLastState(state);

        if (this.initialisingAbortController.signal.aborted) return;
        this.pixelStreaming.connect();
    }

    public destroy(): void {
        this.initialisingAbortController.abort();

        document.removeEventListener("pointerlockchange", this.onPointerLockChange);
        this.containerResizeObserver?.disconnect();

        if (this.pixelStreamingTimeout) {
            clearTimeout(this.pixelStreamingTimeout);
        }

        try {
            const playStreamHandler = () => {
                this.pixelStreaming?.signallingProtocol?.sendMessage({ type: "unsubscribe" });
                this.pixelStreaming?.disconnect();
                this.pixelStreaming?.removeEventListener("playStream", playStreamHandler);
            };
            this.pixelStreaming.addEventListener("playStream", playStreamHandler);
            this.pixelStreaming?.signallingProtocol?.sendMessage({ type: "unsubscribe" });
            this.pixelStreaming?.disconnect();
        } catch (e) {
            // If we destroy too quickly sometimes pixel streaming will throw errors
            // These can be ignored
            console.warn(e);
        }

        this.container.innerHTML = "";

        this.showReconnecting(false);

        // We may not have initialised yet due to waiting for the instance to start up
        this.subscriptions?.forEach((x) => this.removeSubscription(x));

        this.sendEventToStore({
            eventType: MapStoreEventType.EXIT_DIGITAL_TWIN,
        });

        super.destroy();
    }

    // Sends message to the unreal engine and waits for a response of the specified event name
    // Also supports a timeout if the response is not received
    private askUnrealEngine<
        TRequest extends EngineUnrealWebSocketEvents,
        TResponseEventName extends EngineUnrealEventName,
        TPromiseResolveType = ExtractPayloadFromUnrealEvent<TResponseEventName>
    >(
        request: TRequest,
        responseEventName: TResponseEventName,
        timeoutMs = 10000,
        handleResponse?: (resolve: (data: TPromiseResolveType) => void, response: ExtractPayloadFromUnrealEvent<TResponseEventName>) => void
    ): Promise<TPromiseResolveType> {
        return new Promise<TPromiseResolveType>((resolve, reject) => {
            this.sendMessageToUnrealEngine(request);

            const waitEngineResponse = this.pixelStreamingEvents
                .pipe(
                    skipWhile((x) => x.eventName !== responseEventName),
                    timeout(timeoutMs)
                )
                .subscribe(
                    (response) => {
                        const resolvePromise = (resolveData: TPromiseResolveType) => {
                            this.removeSubscription(waitEngineResponse);
                            resolve(resolveData);
                        };

                        if (handleResponse) {
                            handleResponse(resolvePromise, response.payload as ExtractPayloadFromUnrealEvent<TResponseEventName>);
                        } else {
                            resolvePromise(response.payload as TPromiseResolveType);
                        }
                    },
                    (error) => {
                        this.removeSubscription(waitEngineResponse);
                        reject(new Error(`Timeout waiting for engine to reply to ${request.eventName}`));
                    }
                );
            this.subscriptions.push(waitEngineResponse);
        });
    }

    // Returns a listener which listens in the websockets to events from the unreal engine of the specific name
    // and calls the callback function when it is received.
    // Also handles the cleaning up of subscriptions on destroy
    private listenerFromUnrealEngine<TEventName extends EngineUnrealEventName>(
        eventName: TEventName,
        callback: (eventPayload: ExtractPayloadFromUnrealEvent<TEventName>) => void
    ): Listener {
        const eventSubscription = this.pixelStreamingEvents
            .pipe(filter((x) => x.eventName === eventName))
            .subscribe((x) => callback(x.payload as ExtractPayloadFromUnrealEvent<TEventName>));
        this.subscriptions.push(eventSubscription);
        return {
            remove: () => {
                this.removeSubscription(eventSubscription);
            },
        };
    }

    private sendMessageToUnrealEngine<TEvent extends EngineUnrealWebSocketEvents>(message: TEvent): void {
        this.pixelStreaming.emitUIInteraction(message);
    }

    private onMessageReceivedFromUnrealEngine(message: string) {
        try {
            const parsedMessage: EngineUnrealWebSocketEvents = JSON.parse(message);
            this.pixelStreamingEvents.next(parsedMessage);
        } catch (e) {
            createLog("Error parsing message from Unreal Engine", "error", { data: e });
        }
    }

    private onPixelStreamingConnectionSuccess() {
        // Clear timeout
        if (this.pixelStreamingTimeout) {
            clearTimeout(this.pixelStreamingTimeout);
        }

        // Set video resolution
        this.setStreamResolution();

        // Set the position to whatever is stored in state, to support seamless switching between unreal and other map engines
        this.setPosition(this.initialPosition.lat, this.initialPosition.lng, this.initialPosition.bearing, this.initialPosition.pitch, this.initialPosition.zoom);

        const state = this.getCurrentState();

        this.getApiKey().then((response) => {
            this.sendMessageToUnrealEngine({
                eventName: "SET_MAPID",
                payload: {
                    mapId: state.mapId,
                    projectId: this.projectId,
                    apiURL: this.apiURL,
                    apiKey: response,
                },
            });
        });

        // Update the store and update the DB to our user
        this.sendEventToStore({
            eventType: MapStoreEventType.SET_USER_CONNECTED_TO_DIGITAL_TWIN,
            payload: true,
        });
    }

    private onPixelStreamingConnectionFailure(reason: string) {
        createLog("Error connecting to Unreal Engine Pixel Streaming", "error", { data: reason });

        // Full message from Epic: "Streamer ${streamerId} is full. Max players = ${streamer.maxSubscribers}."
        if (reason.includes("Max players")) {
            this.exitMappingEngine(UnrealEngineErrors.OCCUPIED);
        } else {
            this.exitMappingEngine(UnrealEngineErrors.CONNECTION_FAILED);
        }
        this.destroy();
    }

    private setStreamResolution() {
        let { width, height } = this.container.getBoundingClientRect();

        // Whilst preserving aspect ratio, cap the width and height to be 1080 resolution
        const aspectRatio = width / height;
        if (width > 1920 || height > 1080) {
            if (aspectRatio > 1) {
                width = 1920;
                height = width / aspectRatio;
            } else {
                height = 1080;
                width = height * aspectRatio;
            }
        }

        this.pixelStreaming?.emitConsoleCommand(`r.setres ${width}x${height}`);
    }

    private onPointerLockChange = () => {
        const isPointerLocked = document.pointerLockElement != null;
        this.pixelStreaming?.webRtcController.setKeyboardInputEnabled(isPointerLocked);
    };

    private initialiseUnrealListeners() {
        const mapLoadedListener = this.listenerFromUnrealEngine("MAP_LOADED", () => {
            this.onMapLoaded();
        });

        const venueListener = this.listenerFromUnrealEngine("VENUE_ID", (venueId) => {
            // TODO change the component to request the venue id of the current venue rather than requesting
            // the id whenever we set position which could be cumbersome
            this.sendEventToStore({
                eventType: MapStoreEventType.SET_VENUE,
                payload: venueId,
            });
        });

        const mouseReleaseListener = this.listenerFromUnrealEngine("MOUSE_RELEASE", (payload) => {
            // Change mouse mode of the stream
            this.pixelStreaming.config.setFlagEnabled(Flags.HoveringMouseMode, payload);
        });

        this.onDestroy.subscribe(() => {
            mapLoadedListener.remove();
            venueListener.remove();
            mouseReleaseListener.remove();
        });
    }

    // Returns an empty listener for when functions are not used
    private emptyListener(): Listener {
        return {
            remove: () => {},
        };
    }

    public setMapLock(locked: boolean): void {
        throw new Error("Method not implemented.");
    }

    protected removeImage(id: string): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public getExportPosition(): {
        bounds: number[][];
        bearing: number;
        pitch: number;
    } {
        throw new Error("Method not implemented.");
    }

    protected reloadLayersByAssetId(id: string): void {
        throw new Error("Method not implemented.");
    }

    protected onFocus(callback: (event: FocusEvent) => void): Listener {
        return {
            remove: () => {},
        };
    }

    protected onFocusOut(callback: (event: FocusEvent) => void): Listener {
        return {
            remove: () => {},
        };
    }

    public addDateFilterToLayer(layer: MapModuleLayer, dateFilter: { day: number; time: number }): void {
        throw new Error("Method not implemented.");
    }

    public removeDateFilterFromLayer(layer: MapModuleLayer): void {
        throw new Error("Method not implemented.");
    }

    protected moveLayerToTopOfMap(layerId: string): void {
        throw new Error("Method not implemented.");
    }

    protected moveLayerToBottomOfMap(layerId: string): void {
        throw new Error("Method not implemented.");
    }

    protected getUserLocation(): void {
        throw new Error("Method not implemented.");
    }

    public areCompositionObjectsOutsideCadBounds(objects: CompositionMapObject[]): boolean {
        throw new Error("Method not implemented.");
    }

    public areGeometriesOutsideCadBounds(objects: Geometry[]): boolean {
        throw new Error("Method not implemented.");
    }

    protected queryMapObjectsByLayer(position: [number, number], layerIds: string[], radius: number): TypedFeature[] {
        throw new Error("Method not implemented.");
    }

    protected queryMapObjectsForSnapping(position: [number, number], radius: number): TypedFeature[] {
        throw new Error("Method not implemented.");
    }

    protected addSnapIndicatorLayer(): void {
        throw new Error("Method not implemented.");
    }

    protected removeSnapIndicatorLayer(): void {
        throw new Error("Method not implemented.");
    }

    protected setSnapIndicatorSource(position: Position): void {
        throw new Error("Method not implemented.");
    }

    protected setPositionOfSplitLineIndicator(position: { lng: number; lat: number } | undefined): void {
        throw new Error("Method not implemented.");
    }

    protected removeSplitLineIndicator(): void {
        throw new Error("Method not implemented.");
    }

    protected getLayerClass(layerId: string): Layer<TypedFeature[], Partial<AnyLayerStyle>> {
        // Do nothing
        return null;
    }

    protected async onStoreChanged(state: MapState, previousState: MapState) {
        if (state.digitalTwinEngineData.cameraMode.stamp !== previousState.digitalTwinEngineData.cameraMode.stamp) {
            this.setCameraMode(state.digitalTwinEngineData.cameraMode.value);
        }

        super.onStoreChanged(state, previousState);
    }

    private getIpAddress: (digitalTwinLicence: DigitalTwinLicence) => Promise<string>;

    private setCameraMode(mode: DigitalTwinCameraMode): void {
        this.sendMessageToUnrealEngine({
            eventName: "SET_CAMERA_MODE",
            payload: mode,
        });
    }

    private onMapLoaded(): void {
        // Send the local state of layer visibility to the digital twin
        const state = this.getCurrentState();
        state.layers.value.forEach((layer) => {
            if (layer.visible) {
                this.showLayer(layer.id);
            } else {
                this.hideLayer(layer.id);
            }
        });
    }
}
